/**
 * @fileoverview Main entry point for CopilotBar scenario detection system.
 *
 * This module provides the scenario detection mechanism that determines which
 * CopilotBar configuration should be active based on the current application state.
 *
 * @module CopilotBar/scenarios
 */

import { COPILOT_BAR, COPILOT_BAR_LIVE } from 'constants/Features';

import { crudModalScenario, standardCrudScenario } from './crud';
import { dashboardsScenario } from './dashboards';

const SCENARIO_DETECTORS = [
    // Future scenarios can be added here:
    // analyticsScenario,
    // documentAssistantScenario,
    // conversationSummaryScenario,
    {
        ...crudModalScenario,
        featureFlag: COPILOT_BAR_LIVE,
    },
    {
        ...standardCrudScenario,
        featureFlag: COPILOT_BAR_LIVE,
    },
    {
        ...dashboardsScenario,
        featureFlag: COPILOT_BAR,
    },
];

/**
 * Returns an array of scenario detectors ordered by priority.
 *
 * The first detector whose condition evaluates to true will be used to configure
 * the CopilotBar. Each detector is a self-contained unit with:
 *
 * - `scenario` - Identifies the scenario type from {@link COPILOT_BAR_SCENARIOS}
 * - `condition` - Function that determines if this scenario is active based on app state
 * - `buildConfig` - Function that builds the complete CopilotBar configuration
 *
 * @returns {Array<ScenarioDetector>} Array of scenario detector objects in priority order
 *
 * @typedef {Object} ScenarioDetector
 * @property {string} scenario - The scenario type identifier
 * @property {Function} condition - Evaluates app state to determine if scenario is active
 * @property {Function} buildConfig - Builds the CopilotBar configuration for this scenario
 *
 * @example
 * // Adding a new scenario:
 * // 1. Create a new folder: scenarios/analytics/
 * // 2. Define the scenario detector object
 * // 3. Import and add it to the array below
 *
 * // Scenario detector structure:
 * const analyticsScenario = {
 *     scenario: COPILOT_BAR_SCENARIOS.ANALYTICS,
 *     condition: ({ analyticsState }) => analyticsState?.isAssistantOpen,
 *     buildConfig: ({ dispatch, state }) => ({
 *         chatSetup: { ... },
 *         isOpen: true,
 *         onFinish: (payload) => { ... },
 *         pinnedToRight: true,
 *         transition: 'slideUp',
 *         width: 400,
 *     }),
 * };
 */
export const getScenarioDetectors = () => SCENARIO_DETECTORS;
